import React, { useEffect, useMemo, useState } from "react";
import {
  listenDossierRecordsHybrid,
  listenRelationRowsHybrid,
  loadAppSettingsHybrid,
  ensureAppSettings,
} from "../services/data";
import StatWidget from "../components/StatWidget.jsx"; // design inspiré
import TableWidget from "../components/TableWidget.jsx";
import RelationRowEditor from "./RelationRowEditor.jsx";

// -------------------------------------------------
// Utils
// -------------------------------------------------
const ALL_DOSSIERS_KEY = "__ALL__";

const fmtInt = (n) => {
  if (typeof n !== "number" || Number.isNaN(n)) return 0;
  return n;
};

function toMs(d) {
  if (!d) return 0;
  if (typeof d === "number") return d;
  if (d instanceof Date) return +d;
  // Firestore Timestamp
  if (typeof d === "object") {
    if (typeof d.toDate === "function") return +d.toDate();
    if (typeof d.seconds === "number")
      return d.seconds * 1000 + Math.floor((d.nanoseconds || 0) / 1e6);
  }
  if (typeof d === "string") {
    const s = d.trim();

    // 1) dd/mm/yyyy ou dd-mm-yyyy (fr)
    const m1 = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/.exec(s);
    if (m1) {
      let [_, dd, mm, yyyy] = m1;
      const day = parseInt(dd, 10);
      const mon = parseInt(mm, 10) - 1;
      let year = parseInt(yyyy, 10);
      if (year < 100) year += 2000; // 25 -> 2025
      const dt = new Date(year, mon, day);
      return isNaN(+dt) ? 0 : +dt;
    }

    // 2) yyyy-mm-dd (ISO court)
    const m2 = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m2) {
      const [_, y, m, d2] = m2;
      const dt = new Date(
        parseInt(y, 10),
        parseInt(m, 10) - 1,
        parseInt(d2, 10)
      );
      return isNaN(+dt) ? 0 : +dt;
    }

    // 3) fallback - nombres éventuels ("1696550400000", etc.)
    const asNum = Number(s);
    if (!Number.isNaN(asNum)) return asNum;

    // 4) dernier recours : Date.parse (ISO, RFC...)
    const t = Date.parse(s);
    return Number.isNaN(t) ? 0 : t;
  }

  return 0;
}

const formatDate = (ms) => {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleDateString();
  } catch {
    return String(ms);
  }
};

function monthKey(ms) {
  const d = new Date(ms || Date.now());
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(
    2,
    "0"
  )}`;
}

function computeDelta(countsMap) {
  // delta = thisMonth - lastMonth
  const keys = Array.from(countsMap.keys()).sort();
  const n = keys.length;
  if (n < 2) return { delta: 0, positive: true };
  const last = countsMap.get(keys[n - 1]) || 0;
  const prev = countsMap.get(keys[n - 2]) || 0;
  const delta = last - prev;
  return { delta, positive: delta >= 0 };
}

// -------------------------------------------------
// Aggregation engine for KPI (aggregate/metric)
// -------------------------------------------------
function pickTsForRecord(r) {
  const candidates = [
    r?.createdAt,
    r?.updatedAt,
    r?.timestamp,
    r?.ts,
    r?._ts,
    r?.date,
    r?.meta?.createdAt,
    r?.values?.createdAt,
  ];
  for (const c of candidates) {
    if (c == null || c === "") continue;
    if (typeof c === "object") {
      if (typeof c.toDate === "function") return +c.toDate();
      if (typeof c.seconds === "number")
        return c.seconds * 1000 + Math.floor((c.nanoseconds || 0) / 1e6);
    }
    if (typeof c === "number") return c;
    const t = Date.parse(c);
    if (!Number.isNaN(t)) return t;
  }
  if (typeof r?.id === "number") return r.id;
  const n = Number(r?.id);
  if (!Number.isNaN(n)) return n;
  return 0;
}
function pickTsForRelation(row) {
  const cands = [
    row?.updatedAt,
    row?.createdAt,
    row?.timestamp,
    row?.ts,
    row?.date,
  ];
  for (const c of cands) {
    if (c == null || c === "") continue;
    if (typeof c === "object") {
      if (typeof c.toDate === "function") return +c.toDate();
      if (typeof c.seconds === "number")
        return c.seconds * 1000 + Math.floor((c.nanoseconds || 0) / 1e6);
    }
    if (typeof c === "number") return c;
    const t = Date.parse(c);
    if (!Number.isNaN(t)) return t;
  }
  return 0;
}
function inCurrentPeriod(ms, period) {
  if (!ms || period === "all" || !period) return true;
  const d = new Date(ms);
  const now = new Date();
  if (period === "day") {
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  }
  if (period === "month") {
    return (
      d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
    );
  }
  if (period === "year") {
    return d.getFullYear() === now.getFullYear();
  }
  return true;
}

function toNumberLike(v) {
  if (typeof v === "number") return v;
  if (v == null) return NaN;
  let s = String(v)
    .trim()
    .replace(/\u00A0/g, " ")
    .replace(/ /g, "");
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  if (lastComma > lastDot) {
    s = s.replace(/\./g, "").replace(/,/g, ".");
  } else {
    s = s.replace(/,/g, "");
  }
  const n = Number(s);
  return Number.isNaN(n) ? NaN : n;
}
// lit une valeur de champ libre selon la source
function getAnyFieldValue(sourceKind, item, fieldKey) {
  if (!fieldKey) return undefined;
  return sourceKind === "relation"
    ? item?.[fieldKey]
    : item?.values?.[fieldKey] ?? item?.[fieldKey];
}
function applyOneFilter(sourceKind, item, f) {
  if (!f || !f.field || !f.op) return true;
  const a = getAnyFieldValue(sourceKind, item, f.field);
  const bRaw = f.value;
  const op = f.op;

  if (op === "contains") {
    const A = (a ?? "").toString().toLowerCase();
    const B = (bRaw ?? "").toString().toLowerCase();
    return A.includes(B);
  }

  const na = toNumberLike(a);
  const nb = toNumberLike(bRaw);

  switch (op) {
    case "=":
      return (a ?? "") == (bRaw ?? "");
    case "!=":
      return (a ?? "") != (bRaw ?? "");
    case ">":
      return !Number.isNaN(na) && !Number.isNaN(nb) ? na > nb : false;
    case ">=":
      return !Number.isNaN(na) && !Number.isNaN(nb) ? na >= nb : false;
    case "<":
      return !Number.isNaN(na) && !Number.isNaN(nb) ? na < nb : false;
    case "<=":
      return !Number.isNaN(na) && !Number.isNaN(nb) ? na <= nb : false;
    default:
      return true;
  }
}
function applyFilters(sourceKind, items, filters) {
  const arr = Array.isArray(filters) ? filters : [];
  const active = arr.filter(
    (f) => (f?.action || "filter").toLowerCase() !== "highlight"
  );
  if (!active.length) return items;
  return items.filter((it) =>
    active.every((f) => applyOneFilter(sourceKind, it, f))
  );
}
function parseColumnsKeys(s) {
  return (s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}
function getFieldValue(sourceKind, item, fieldKey) {
  if (!fieldKey) return undefined;
  if (sourceKind === "relation") return item?.[fieldKey];
  // dossier -> values[fieldKey] then direct
  return item?.values?.[fieldKey] ?? item?.[fieldKey];
}

const DEFAULT_ROW_COLOR = "#ef4444";
const normalizeHexColor = (color) => {
  if (typeof color !== "string") return DEFAULT_ROW_COLOR;
  const trimmed = color.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    const [hash, r, g, b] = trimmed;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return DEFAULT_ROW_COLOR;
};
const hexToRgba = (hex, alpha = 0.16) => {
  const norm = normalizeHexColor(hex);
  const r = parseInt(norm.slice(1, 3), 16);
  const g = parseInt(norm.slice(3, 5), 16);
  const b = parseInt(norm.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
const buildCellHighlight = (filter) => {
  const color = normalizeHexColor(filter?.color);
  return {
    backgroundColor: hexToRgba(color, 0.25),
    textColor: color,
  };
};

function applyFilterPredicate(sourceKind, item, filter) {
  if (!filter || !filter.field || !filter.op) return true;
  const a =
    sourceKind === "relation"
      ? item?.[filter.field]
      : item?.values?.[filter.field] ?? item?.[filter.field];
  const bRaw = filter.value;
  const op = filter.op;

  if (op === "contains") {
    const A = (a ?? "").toString().toLowerCase();
    const B = (bRaw ?? "").toString().toLowerCase();
    return A.includes(B);
  }

  // try numeric first for >,>=,<,<=
  const na = toNumberLike(a);
  const nb = toNumberLike(bRaw);

  switch (op) {
    case "=":
      return (a ?? "") == (bRaw ?? "");
    case "!=":
      return (a ?? "") != (bRaw ?? "");
    case ">":
      return !Number.isNaN(na) && !Number.isNaN(nb) ? na > nb : false;
    case ">=":
      return !Number.isNaN(na) && !Number.isNaN(nb) ? na >= nb : false;
    case "<":
      return !Number.isNaN(na) && !Number.isNaN(nb) ? na < nb : false;
    case "<=":
      return !Number.isNaN(na) && !Number.isNaN(nb) ? na <= nb : false;
    default:
      return true;
  }
}

export function runAggregateMetric({
  sourceKind = "dossier",
  dossierKey = "",
  typeKey = "",
  period = "all",
  aggregate = "COUNT",
  fieldKey = "",
  filter = null,
  itemsOverride = null,
}) {
  const src = sourceKind === "relation" ? "relation" : "dossier";
  // si on nous donne les items (Firestore), on les utilise; sinon fallback (jamais appelé désormais)
  let items = Array.isArray(itemsOverride) ? itemsOverride.slice() : [];
  if (!itemsOverride) {
    items = []; // garde une valeur sûre
  }

  // period filter
  items = items.filter((it) => {
    const ms = src === "relation" ? pickTsForRelation(it) : pickTsForRecord(it);
    return inCurrentPeriod(ms, period);
  });

  // builder filter
  items = items.filter((it) => applyFilterPredicate(src, it, filter));

  // aggregate
  if (aggregate === "COUNT") {
    return {
      meta: { aggregate, groupBy: null, total: items.length },
      groups: [],
    };
  }

  // SUM / AVG over fieldKey
  let sum = 0;
  let cnt = 0;
  for (const it of items) {
    const v = getFieldValue(src, it, fieldKey);
    const n = toNumberLike(v);
    if (!Number.isNaN(n)) {
      sum += n;
      cnt += 1;
    }
  }
  const total = aggregate === "AVG" ? (cnt ? sum / cnt : 0) : sum;
  return { meta: { aggregate, groupBy: null, total }, groups: [] };
}
// Helpers pour labels/tri comme RelationPage
const safeLabel = (f) => f?.name || f?.label || f?.key || "";
const byOrderThenLabel = (a, b) =>
  Number(a?.order ?? 0) - Number(b?.order ?? 0) ||
  safeLabel(a).localeCompare(safeLabel(b));
function getApplicableFieldsFor(typeKey, relationTypesArr, relationFieldsArr) {
  const currentType = (relationTypesArr || []).find(
    (t) => t.key === typeKey
  ) || { id: null, key: typeKey };
  return (relationFieldsArr || [])
    .filter((f) => {
      const ids = f?.applicableIds || [];
      return (
        ids.length === 0 ||
        ids.includes(currentType.id) ||
        ids.includes(currentType.key)
      );
    })
    .sort(byOrderThenLabel);
}

// -------------------------------------------------
// Widget component (includes previous types + new aggregate)
// -------------------------------------------------
export default function Widget({
  user,
  widget,
  onNavigate,
  onEditRecord,
  className = "",
  style = {},
  fullScreen = false,
}) {
  const type = widget?.type || "";
  // --- états Firestore ---
  const [dossierTypes, setDossierTypes] = useState([]);
  const [relationTypes, setRelationTypes] = useState([]);

  const [dossierRecords, setDossierRecords] = useState([]); // fusion multi-dossiers si besoin
  const [relations, setRelations] = useState([]); // fusion multi-types si besoin
  const [relEditor, setRelEditor] = useState({
    open: false,
    typeKey: null,
    row: null,
  });
  const title = widget?.title || "";
  const props = widget?.props || {};
  const [relationFields, setRelationFields] = useState([]);

  //const dossierRecords = useMemo(() => filterDossierRecords({ dossierKey: props.dossierKey || "", statut: props.statut || "" }), [props.dossierKey, props.statut]);
  //const relations = useMemo(() => getRelations(props.typeKey || ""), [props.typeKey]);
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = loadAppSettingsHybrid(user.uid, async (app) => {
      if (!app) {
        try {
          app = await ensureAppSettings(user.uid);
        } catch {}
      }
      setDossierTypes(app?.dossiers?.types || []);
      setRelationTypes(app?.relations?.types || []);
      setRelationFields(app?.relations?.fields || []);
    });
    return () => {
      try {
        unsub && unsub();
      } catch {}
    };
  }, [user?.uid]);
  useEffect(() => {
    if (!user?.uid) return;
    // on écoute 1..N dossiers selon props.dossierKey
    const selected = widget?.props?.dossierKey;
    const keys =
      selected && selected !== ALL_DOSSIERS_KEY
        ? [selected]
        : (dossierTypes || []).map((t) => t.key);

    if (!keys.length) {
      setDossierRecords([]);
      return;
    }

    const buckets = {};
    const push = () => {
      const merged = Object.values(buckets).flat();
      setDossierRecords(merged);
    };

    const unsubs = keys.map((k) =>
      listenDossierRecordsHybrid(
        user.uid,
        {
          dossierKey: k,
          statut: (widget?.props?.statut || "").trim() || null, // filtre serveur léger
        },
        (rows) => {
          buckets[k] = rows || [];
          push();
        }
      )
    );
    return () => {
      unsubs.forEach((u) => {
        try {
          u && u();
        } catch {}
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.uid,
    widget?.props?.dossierKey,
    widget?.props?.statut,
    JSON.stringify(dossierTypes.map((t) => t.key)),
  ]);
  useEffect(() => {
    if (!user?.uid) return;
    const tk = widget?.props?.typeKey;
    const keys = tk ? [tk] : (relationTypes || []).map((t) => t.key);

    if (!keys.length) {
      setRelations([]);
      return;
    }

    const buckets = {};
    const push = () => setRelations(Object.values(buckets).flat());

    const unsubs = keys.map((k) =>
      listenRelationRowsHybrid(user.uid, k, (rows) => {
        buckets[k] = rows || [];
        push();
      })
    );
    return () => {
      unsubs.forEach((u) => {
        try {
          u && u();
        } catch {}
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.uid,
    widget?.props?.typeKey,
    JSON.stringify(relationTypes.map((t) => t.key)),
  ]);

  // Precompute month counts for delta
  const monthCounts = useMemo(() => {
    const map = new Map();
    const list =
      type === "kpi_count" && props.scope === "relation"
        ? relations
        : dossierRecords;
    for (const r of list) {
      const ms =
        type === "kpi_count" && props.scope === "relation"
          ? pickTsForRelation(r)
          : pickTsForRecord(r);
      if (!ms) continue;
      const mk = monthKey(ms);
      map.set(mk, (map.get(mk) || 0) + 1);
    }
    return map;
  }, [type, props.scope, dossierRecords, relations]);

  if (type === "kpi_count") {
    const scope = props.scope === "relation" ? "relation" : "dossier";
    const count =
      scope === "relation" ? relations.length : dossierRecords.length;
    const { delta, positive } = computeDelta(monthCounts);
    return (
      <StatWidget
        title={title || (scope === "relation" ? "Relations" : "Documents")}
        value={fmtInt(count)}
        label={
          scope === "relation"
            ? props.typeKey || "Tous types"
            : props.dossierKey || "Tous dossiers"
        }
        change={`${delta >= 0 ? "+" : ""}${delta} ce mois`}
        positive={positive}
      />
    );
  }

  if (type === "relations_kpi") {
    const count = relations.length;
    const { delta, positive } = computeDelta(monthCounts);
    return (
      <StatWidget
        title={title || "Relations"}
        value={fmtInt(count)}
        label={props.typeKey || "Tous types"}
        change={`${delta >= 0 ? "+" : ""}${delta} ce mois`}
        positive={positive}
      />
    );
  }

  if (type === "recent_records") {
    const limit = Math.max(1, Math.min(50, Number(props.limit) || 5));
    const items = [...dossierRecords]
      .map((r) => ({
        ...r,
        __ms: toMs(r?.createdAt ?? r?.created ?? r?.time ?? r?.ts),
      }))
      .sort((a, b) => b.__ms - a.__ms)
      .slice(0, limit);

    const rows = items.map((r) => ({
      name:
        r?.docName ||
        r?.filename ||
        r?.title ||
        r?.nom ||
        r?.name ||
        (r?.id
          ? `Enregistrement ${String(r.id).slice(0, 6)}`
          : "Enregistrement"),
      client: r?.client || r?.relationName || r?.dest || "-",
      date: formatDate(r.__ms),
      type: props.dossierKey || r?.type || "-",
      status: r?.statut || r?.status || "-",
      actions: {
        onEdit: () =>
          onEditRecord
            ? onEditRecord(r)
            : onNavigate && onNavigate("useModele"),
      },
    }));

    const filtersUI = (
      <>
        <select className="filter-select" defaultValue="Tous les types">
          <option>Tous les types</option>
          <option>Contrats</option>
          <option>Devis</option>
          <option>Factures</option>
        </select>
        <select className="filter-select" defaultValue="Tous les statuts">
          <option>Tous les statuts</option>
          <option>Complété</option>
          <option>En attente</option>
          <option>Brouillon</option>
        </select>
      </>
    );
    return (
      <TableWidget
        title={title || "Documents récents"}
        rows={rows}
        rightSlot={filtersUI}
      />
    );
  }

  if (type === "quick_actions") {
    const showUpload = props.showUpload !== false;
    const showImportRelations = props.showImportRelations !== false;
    const firstRelType = relationTypes?.[0]?.key;

    return (
      <div className={`widget stat-widget ${className}`} style={{ ...style }}>
        <div className="widget-header">
          <h3 className="widget-title">{title || "Actions rapides"}</h3>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {showUpload && (
            <button
              className="btn btn-primary"
              onClick={() => onNavigate && onNavigate("uploadModele")}
            >
              <i className="fas fa-file-upload" /> Nouveau modèle
            </button>
          )}
          {showImportRelations && (
            <button
              className="btn btn-outline"
              onClick={() =>
                firstRelType && onNavigate && onNavigate(firstRelType)
              }
            >
              <i className="fas fa-file-import" /> Importer CSV (relations)
            </button>
          )}
        </div>
      </div>
    );
  }

  if (type === "chart_by_month") {
    // On conserve ton mini-chart existant si besoin, sinon tu peux brancher une lib plus tard
    return (
      <div className={`widget stat-widget ${className}`} style={{ ...style }}>
        <div className="widget-header">
          <h3 className="widget-title">{title || "Documents par mois"}</h3>
        </div>
        <div style={{ color: "#6b7280" }}>
          Graph à brancher ici (conserve la data côté Dashboard).
        </div>
      </div>
    );
  }

  // -------------------------------
  // NOUVEAU: KPI Agrégat (metric)
  // -------------------------------
  if (type === "aggregate" && (props.display === "metric" || !props.display)) {
    const baseItems = props.sourceKind === "relation" ? relations : dossierRecords;
    const res = runAggregateMetric({
      sourceKind: props.sourceKind || "dossier",
      dossierKey: props.dossierKey || "",
      typeKey: props.typeKey || "",
      period: props.period || "all",
      aggregate: props.aggregate || "COUNT",
      fieldKey: props.fieldKey || "",
      filter: props.filter || null,
      itemsOverride: baseItems,
    });

    const value = res?.meta?.total ?? 0;
    const rawLabel = (props.labelText || "").trim();
    const label = rawLabel
      ? rawLabel
      : props.sourceKind === "relation"
      ? props.typeKey || "Relations"
      : !props.dossierKey || props.dossierKey === ALL_DOSSIERS_KEY
      ? "Tous dossiers"
      : props.dossierKey;

    // Choose color/icon based on selected aggregate type
    const getAggregateStyle = () => {
      const aggregateType = props.aggregate || "COUNT";
      switch (aggregateType) {
        case "SUM":
          return {
            colorClass: "secondary",
            icon: "fa-dollar-sign",
            label: "Somme",
          };
        case "AVG":
          return {
            colorClass: "info",
            icon: "fa-chart-line",
            label: "Moyenne",
          };
        case "COUNT":
        default:
          return {
            colorClass: "primary",
            icon: "fa-layer-group",
            label: "Total",
          };
      }
    };

    const metricStyle = getAggregateStyle();

    return (
      <div className={`kpi-card ${metricStyle.colorClass}`}>
        <div className="kpi-header">
          <div className="kpi-title">{title || metricStyle.label}</div>
          <div className="kpi-icon">
            <i className={`fas ${metricStyle.icon}`}></i>
          </div>
        </div>
        <div className="kpi-value">{fmtInt(value)}</div>
        <div className="kpi-label">{label}</div>
      </div>
    );
  }
  // -------------------------------
  // NOUVEAU: Tableau (table_list)
  // -------------------------------
  if (type === "table_list" || type === "relation_table") {
    const sourceKind =
      type === "relation_table"
        ? "relation"
        : props.sourceKind === "relation"
        ? "relation"
        : "dossier";
    const period = props.period || "all";
    const filters = Array.isArray(props.filters) ? props.filters : [];
    const highlightFilters = filters.filter(
      (f) => (f?.action || "filter").toLowerCase() === "highlight"
    );
    const columnsKeys = parseColumnsKeys(props.columnsKeys || "");
    const sumKeys = Array.isArray(props.sumKeys) ? props.sumKeys : [];
    const deadlineKey = props.deadlineKey || "";
    const warnDays = Number(props.warnDays ?? 7);
    const dangerDays = Number(props.dangerDays ?? 2);
    const blink = !!props.blink;

    let items =
      sourceKind === "dossier" ? dossierRecords.slice() : relations.slice();
    // Si l'utilisateur a fixé un dossier/type précis dans la conf du widget, on filtre :
    if (
      sourceKind === "dossier" &&
      props.dossierKey &&
      props.dossierKey !== ALL_DOSSIERS_KEY
    ) {
      items = items.filter((r) => r?.dossierKey === props.dossierKey);
    }
    if (sourceKind === "relation" && props.typeKey) {
      // déjà abonné au bon type si typeKey existait; si "tous", on ne filtre pas
    }
    // période
    items = items.filter((r) =>
      inCurrentPeriod(
        sourceKind === "relation" ? pickTsForRelation(r) : pickTsForRecord(r),
        period
      )
    );
    // filtres avancés
    items = applyFilters(sourceKind, items, filters);

    // Colonnes finales
    // - Dossier: [__fileName, ...columnsKeys, __actions]
    // - Relation: [...columnsKeys, __actions]
    const columns = [];
    columnsKeys.forEach((k) => columns.push({ key: k, label: k }));
    columns.push({ key: "__actions", label: "Actions", isActions: true });

    // Lignes
    const rows = items.map((r, idx) => {
      // nom de fichier
      const fileName =
        r?.fileName ||
        r?.docName ||
        r?.filename ||
        r?.title ||
        r?.nom ||
        r?.name ||
        (r?.id ? String(r.id) : "-");

      // deadline
      let deadlineMs = 0;
      if (deadlineKey) {
        const raw = getAnyFieldValue(sourceKind, r, deadlineKey);
        const t = toMs(raw);
        deadlineMs = t;
      }
      let deadlineStatus = "none";
      if (deadlineMs) {
        // on compare à minuit (pas à l'heure courante)
        const DAY = 24 * 3600 * 1000;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dline = new Date(deadlineMs);
        dline.setHours(0, 0, 0, 0);
        const daysDiff = Math.floor((dline - today) / DAY);

        if (daysDiff < 0) {
          // échu (date passée) => expired (pas de clignotement)
          deadlineStatus = "expired";
        } else if (daysDiff <= dangerDays) {
          deadlineStatus = "danger";
        } else if (daysDiff <= warnDays) {
          deadlineStatus = "warn";
        } else {
          deadlineStatus = "ok";
        }
      }
      // construire la ligne
      const row = { __id: idx, __deadline: deadlineStatus, __original: r }; // [new] garde la ligne brute
      // champ de tri caché pour la colonne deadline
      if (deadlineKey) {
        row["__sort_deadline"] = Number(deadlineMs || 0);
      }
      // colonnes dynamiques depuis columnsKeys
      for (const k of columnsKeys) {
        const v = getAnyFieldValue(sourceKind, r, k);
        // si c'est la colonne deadlineKey, on formate en date lisible
        row[k] = k === deadlineKey ? formatDate(toMs(v)) || "" : v ?? "";
      }
      if (highlightFilters.length) {
        const cellHighlights = {};
        highlightFilters.forEach((hf) => {
          if (!hf?.field) return;
          if (applyOneFilter(sourceKind, r, hf)) {
            cellHighlights[hf.field] = buildCellHighlight(hf);
          }
        });
        if (Object.keys(cellHighlights).length) {
          row.__cellHighlights = cellHighlights;
        }
      }

      row.__meta = {
        sourceKind,
        typeKey: props.typeKey || null,
        dossierKey: props.dossierKey || null,
      };
      row["__actions"] = {
        onEdit: () => {
          if (sourceKind === "relation") {
            const tk =
              props.typeKey ||
              row.__meta?.typeKey ||
              widget?.config?.typeKey ||
              null;
            setRelEditor({
              open: true,
              typeKey: tk,
              row: row.__original,
            });
            return;
          }
          if (onEditRecord) return onEditRecord(row.__original, row.__meta);
          if (onNavigate) return onNavigate("useModele");
        },
      };

      return row;
    });

    // Sommes (pour les clés cochées)
    const footerSums = {};
    for (const k of sumKeys) {
      let s = 0;
      for (const row of rows) {
        const n = toNumberLike(row[k]);
        if (!Number.isNaN(n)) s += n;
      }
      footerSums[k] = s;
    }

    // Passage au composant
    const rowsPerPage = fullScreen
      ? Math.max(Number(props.pageSize || 7), 20)
      : Number(props.pageSize || 7);

    const cardClasses = ["widget-card", fullScreen ? "full-screen" : "", className]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={cardClasses} style={{ ...style }}>
        <TableWidget
          title={title || "Tableau"}
          columns={columns}
          rows={rows}
          footerSums={footerSums}
          deadlineKey={deadlineKey}
          blinkDanger={blink}
          pageSize={rowsPerPage}
          thresholds={Array.isArray(props.thresholds) ? props.thresholds : []}
          fullScreen={fullScreen}
        />
        {relEditor.open && relEditor.typeKey && relEditor.row && (
          <RelationRowEditor
            user={user}
            typeKey={relEditor.typeKey}
            row={relEditor.row}
            fieldsMeta={getApplicableFieldsFor(
              relEditor.typeKey,
              relationTypes,
              relationFields
            )}
            onClose={() =>
              setRelEditor({ open: false, typeKey: null, row: null })
            }
            onSaved={() =>
              setRelEditor({ open: false, typeKey: null, row: null })
            }
          />
        )}
      </div>
    );
  }

  return (
    <div className={`widget stat-widget ${className}`} style={{ ...style }}>
      <div className="widget-header">
        <h3 className="widget-title">{title || "Widget"}</h3>
      </div>
      <div style={{ color: "#6b7280" }}>
        Type de widget inconnu: <code>{String(type)}</code>
      </div>
    </div>
  );
}

